/**
 * Shared allocation derivation + validation checks for sku-allocation rows.
 *
 * - K Final_% / L Eligible_Final_%: override → new product override → 0 (no LTM)
 * - M Subcategory_Total_Eligible_%: SUMIF(subCategory, eligibleFinalPercent), rounded to 4 dp (true sum, no cap)
 * - N Adjusted_Final_%: IFERROR(eligible / subcategoryTotal, 0)
 */

const MONTH_REVENUE_KEYS = [
    'janRevenue',
    'febRevenue',
    'marRevenue',
    'aprRevenue',
    'mayRevenue',
    'junRevenue',
    'julRevenue',
    'augRevenue',
    'sepRevenue',
    'octRevenue',
    'novRevenue',
    'decRevenue',
];

const MONTH_UNIT_KEYS = [
    'janUnits',
    'febUnits',
    'marUnits',
    'aprUnits',
    'mayUnits',
    'junUnits',
    'julUnits',
    'augUnits',
    'sepUnits',
    'octUnits',
    'novUnits',
    'decUnits',
];

const PERCENT_TOLERANCE = 0.0005;
/** Per-SKU percent columns (K, L): ROUND(..., 6). */
const PERCENT_FRACTION_DECIMALS = 4;
const PERCENT_AGGREGATE_SCALE = 10 ** PERCENT_FRACTION_DECIMALS;
/** Subcategory total (M): fraction (1 = 100%), rounded to 4 dp — true SUMIF, never capped. */
const SUBCATEGORY_TOTAL_FRACTION_DECIMALS = 4;

function roundPercentFraction(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Number(n.toFixed(PERCENT_FRACTION_DECIMALS));
}

function roundSubcategoryEligibleTotal(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Number(n.toFixed(SUBCATEGORY_TOTAL_FRACTION_DECIMALS));
}

function toEligibleAggregateMicro(fraction) {
    const rounded = roundPercentFraction(fraction);
    return Math.round(rounded * PERCENT_AGGREGATE_SCALE + 1e-9);
}

function subcategoryEligibleTotalFromMicro(totalMicro) {
    return roundSubcategoryEligibleTotal(totalMicro / PERCENT_AGGREGATE_SCALE);
}

function isPercentSet(value) {
    if (value == null) return false;
    const trimmed = String(value).trim();
    if (trimmed === '' || trimmed === '-') return false;
    return Number.isFinite(Number(value));
}

function readPercentFraction(value) {
    if (!isPercentSet(value)) return null;
    return Number(value);
}

/** Override → new product override → 0. No LTM fallback (K and L). */
function computeOverrideBasedPercent(row) {
    const override = readPercentFraction(row.overridePercent);
    if (override != null) return roundPercentFraction(override);

    const newProduct =
        readPercentFraction(row.newProductOverride) ??
        readPercentFraction(row.newProductOverridePercent);
    if (newProduct != null) return roundPercentFraction(newProduct);

    return 0;
}

function computeFinalPercent(row) {
    return computeOverrideBasedPercent(row);
}

function computeEligibleFinalPercent(row) {
    return computeOverrideBasedPercent(row);
}

function hasOverrideConflict(row) {
    return (
        isPercentSet(row.overridePercent) &&
        (isPercentSet(row.newProductOverride) || isPercentSet(row.newProductOverridePercent))
    );
}

function isEmptySkuRow(row) {
    const sku = String(row?.supplySku ?? row?.sku ?? '').trim();
    return !sku || sku === '-';
}

function subcategoryKey(row) {
    return row?.subCategory || 'Unassigned';
}

/** SUMIF(subCategory, eligibleFinalPercent). */
function buildSubcategoryEligibleTotalsMap(rows) {
    const microBySub = new Map();
    for (const record of rows || []) {
        if (isEmptySkuRow(record)) continue;
        const subKey = subcategoryKey(record);
        const eligibleMicro = toEligibleAggregateMicro(computeEligibleFinalPercent(record));
        microBySub.set(subKey, (microBySub.get(subKey) || 0) + eligibleMicro);
    }
    const map = new Map();
    for (const [subKey, totalMicro] of microBySub) {
        map.set(subKey, subcategoryEligibleTotalFromMicro(totalMicro));
    }
    return map;
}

function normalizeSubcategoryTotalsMap(input) {
    if (input instanceof Map) {
        return new Map(
            [...input.entries()].map(([key, value]) => [
                key,
                roundSubcategoryEligibleTotal(Number(value) || 0),
            ])
        );
    }
    if (input && typeof input === 'object') {
        return new Map(
            Object.entries(input).map(([key, value]) => [
                key,
                roundSubcategoryEligibleTotal(Number(value) || 0),
            ])
        );
    }
    return null;
}

function subcategoryEligibleTotalsMapFromTablePayload(tablePayload) {
    return normalizeSubcategoryTotalsMap(
        tablePayload?.subcategoryEligibleTotalsBySubCategory
    );
}

function computeSubcategoryEligibleTotalPercent(row, subcategoryEligibleTotals) {
    if (isEmptySkuRow(row)) return null;
    return roundSubcategoryEligibleTotal(subcategoryEligibleTotals.get(subcategoryKey(row)) ?? 0);
}

function computeAdjustedFinalPercent(eligibleFinalPercent, subcategoryEligibleTotalPercent, row) {
    if (row && isEmptySkuRow(row)) return null;
    const eligible = roundPercentFraction(eligibleFinalPercent);
    const denom = roundSubcategoryEligibleTotal(subcategoryEligibleTotalPercent);
    if (denom <= 0) return 0;
    return roundPercentFraction(eligible / denom);
}

/** Inverse of adjusted final %: override/eligible fraction from adjusted share × subcategory total. */
function adjustedFinalPercentToEligibleOverride(adjustedFinalPercent, subcategoryEligibleTotalPercent) {
    const adjusted = roundPercentFraction(adjustedFinalPercent);
    const denom = roundSubcategoryEligibleTotal(subcategoryEligibleTotalPercent);
    if (denom <= 0) return adjusted;
    return roundPercentFraction(adjusted * denom);
}

function sumMonthlyRevenueFromRow(row) {
    return MONTH_REVENUE_KEYS.reduce((sum, key) => sum + Number(row[key] || 0), 0);
}

function parseMsrpValue(raw) {
    if (raw == null) return null;
    const trimmed = String(raw).trim();
    if (trimmed === '' || trimmed === '-') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
}

function hasMonthlyUnitsOnRecord(record) {
    return MONTH_UNIT_KEYS.some((key) => record[key] != null && record[key] !== '');
}

/** Sum of `janUnits`–`decUnits` for the Total Units column (numeric only). */
function computeTotalUnitsSummary(record) {
    if (hasMonthlyUnitsOnRecord(record)) {
        let total = 0;
        for (let i = 0; i < 12; i += 1) {
            const raw = record[MONTH_UNIT_KEYS[i]];
            if (raw == null || raw === '') continue;
            total += Number((Number(raw) || 0).toFixed(2));
        }
        return Number(Number(total).toFixed(2));
    }
    return null;
}

function computeForecastInputCheck(record) {
    const msrp = parseMsrpValue(record?.msrp);
    if (msrp == null) return 'Missing Price';
    if (msrp === 0) return 'Price = 0';

    const skuForecastRevenue = Number(record?.skuForecastRevenue || 0);
    if (skuForecastRevenue === 0) return 'No Forecast Revenue';

    const annualUnits = Number(record?.annualUnits || 0);
    if (annualUnits === 0) return 'Units = 0';

    return 'OK';
}

function deriveSkuAllocationChecks(baseRows, options = {}) {
    const precomputed = normalizeSubcategoryTotalsMap(options.subcategoryEligibleTotalsMap);
    const subcategoryEligibleTotals =
        precomputed ??
        (options.allRowsForSubcategoryTotals != null
            ? buildSubcategoryEligibleTotalsMap(options.allRowsForSubcategoryTotals)
            : buildSubcategoryEligibleTotalsMap(baseRows));

    const pass1 = baseRows.map((record) => {
        const finalPercent = computeFinalPercent(record);
        const eligibleFinalPercent = isEmptySkuRow(record)
            ? null
            : computeEligibleFinalPercent(record);

        const subcategoryEligibleTotalPercent = computeSubcategoryEligibleTotalPercent(
            record,
            subcategoryEligibleTotals
        );

        const adjustedFinalPercent = computeAdjustedFinalPercent(
            eligibleFinalPercent,
            subcategoryEligibleTotalPercent,
            record
        );

        const totalRevenue = sumMonthlyRevenueFromRow(record);
        const recalcTotalFromMonths = Number(totalRevenue.toFixed(2));
        const readRevenueMetric = (value) => {
            if (value == null) return null;
            const trimmed = String(value).trim();
            if (trimmed === '' || trimmed === '-') return null;
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : null;
        };
        const recalcTotal2025 =
            readRevenueMetric(record.recalcTotal2025) ?? recalcTotalFromMonths;
        const overrideConflictCheck = hasOverrideConflict(record) ? 'ERROR' : 'OK';
        const forecastableCheck =
            !record.forecastable &&
            adjustedFinalPercent != null &&
            adjustedFinalPercent > PERCENT_TOLERANCE
                ? 'ERROR'
                : 'OK';
        const forecastInputCheck = computeForecastInputCheck(record);

        const totalUnitsCheck = computeTotalUnitsSummary(record);

        return {
            ...record,
            finalPercent,
            eligibleFinalPercent,
            subcategoryEligibleTotalPercent,
            adjustedFinalPercent,
            totalRevenue: recalcTotal2025,
            recalcTotal2025,
            overrideConflictCheck,
            forecastableCheck,
            forecastInputCheck,
            allocationTotalCheck: 'OK',
            totalUnitsCheck,
        };
    });

    return pass1.map((record) => {
        const subKey = subcategoryKey(record);
        const subcategoryEligibleTotalPercent =
            computeSubcategoryEligibleTotalPercent(record, subcategoryEligibleTotals) ?? 0;
        const rowsInSubcategory = baseRows.filter(
            (candidate) => subcategoryKey(candidate) === subKey && !isEmptySkuRow(candidate)
        );
        const eligibleSumInView = rowsInSubcategory.reduce(
            (sum, candidate) => sum + computeEligibleFinalPercent(candidate),
            0
        );
        const hasFullSubcategoryInView =
            Math.abs(
                roundSubcategoryEligibleTotal(eligibleSumInView) -
                    subcategoryEligibleTotalPercent
            ) <= PERCENT_TOLERANCE;
        const subcategoryAdjustedTotal = rowsInSubcategory
            .map((candidate) => {
                const eligible = computeEligibleFinalPercent(candidate);
                return (
                    computeAdjustedFinalPercent(
                        eligible,
                        subcategoryEligibleTotalPercent,
                        candidate
                    ) ?? 0
                );
            })
            .reduce((sum, value) => sum + value, 0);

        const allocationTotalCheck = 'OK';

        return {
            ...record,
            allocationTotalCheck,
        };
    });
}

module.exports = {
    deriveSkuAllocationChecks,
    computeFinalPercent,
    computeEligibleFinalPercent,
    computeOverrideBasedPercent,
    computeSubcategoryEligibleTotalPercent,
    computeAdjustedFinalPercent,
    adjustedFinalPercentToEligibleOverride,
    computeForecastInputCheck,
    computeTotalUnitsSummary,
    buildSubcategoryEligibleTotalsMap,
    subcategoryEligibleTotalsMapFromTablePayload,
    roundPercentFraction,
    roundSubcategoryEligibleTotal,
    toEligibleAggregateMicro,
    subcategoryEligibleTotalFromMicro,
    MONTH_REVENUE_KEYS,
    MONTH_UNIT_KEYS,
    PERCENT_TOLERANCE,
    PERCENT_FRACTION_DECIMALS,
    SUBCATEGORY_TOTAL_FRACTION_DECIMALS,
    PERCENT_AGGREGATE_SCALE,
};
